import {
  CalendarVariable,
  DoBoxVariable,
  DoBoxVariablePlain,
  EmailVariable,
  EmailVariablePlain,
  FacebookMessengerVariablePlain,
  FacebookVariablePlain,
  GitHubVariablePlain,
  GoogleChatVariablePlain,
  InstagramVariablePlain,
  SlackVariablePlain,
  StarterBrainSignalVariable,
  StarterCalendarVariable,
  StarterFacebookMessengerVariable,
  StarterFacebookVariable,
  StarterGbpVariable,
  StarterGitHubVariable,
  StarterGoogleChatVariable,
  StarterInstagramVariable,
  StarterKnowledgeVariable,
  StarterManualVariable,
  StarterPeopleVariable,
  StarterScheduleVariable,
  StarterSlackVariable,
  StarterTeamsVariable,
  StarterWhatsAppVariable,
  TeamsVariablePlain,
  WhatsAppVariablePlain,
} from './flow';
import { TriggerType } from './userCommunicationAgent';

/**
 * Story-reply-only Instagram keys and the INSTAGRAM_CONTENT keys. They have no
 * entry in StarterInstagramVariable because they do not exist on a plain DM,
 * and they lived in apps/api until they became part of this registry.
 */
export const StarterInstagramStoryVariable = {
  STORY_ID: 'starter.instagram.storyId',
  STORY_URL: 'starter.instagram.storyUrl',
} as const;

export const StarterInstagramContentVariable = {
  POST_ID: 'starter.instagramContent.postId',
  COMMENT_ID: 'starter.instagramContent.commentId',
  PARENT_COMMENT_ID: 'starter.instagramContent.parentCommentId',
  COMMENT_TEXT: 'starter.instagramContent.commentText',
  AUTHOR_USERNAME: 'starter.instagramContent.authorUsername',
} as const;

/**
 * Who wrote the value. `external` is text from anyone outside the workspace user,
 * first- or second-hand (a model rewrite of inbound mail is still external).
 * `system` is an id, timestamp or field the platform sets. `workspace` is text
 * the user themself authored; nothing in the registry is marked so today, because
 * no trigger field is provably the user's own.
 */
export const StarterVariableTrust = {
  EXTERNAL: 'external',
  WORKSPACE: 'workspace',
  SYSTEM: 'system',
} as const;

export type StarterVariableTrust =
  (typeof StarterVariableTrust)[keyof typeof StarterVariableTrust];

export interface StarterVariableDef {
  /** Canonical run-time key. Always the form a resolution strategy seeds. */
  key: string;
  /**
   * Plain `<namespace>.<field>` twin, declared only where a strategy seeds both.
   * Absent means the plain form does not resolve — the editor must never emit it.
   */
  alias?: string;
  labelKey: string;
  trust: StarterVariableTrust;
  /**
   * Legitimately absent on some fires of this trigger type, so the seed-coverage
   * check skips it. Judged per TriggerType, not per builder: the
   * instagramContent keys look conditional inside buildCommunicationSeedContext
   * only because that function serves every family at once.
   */
  optional?: boolean;
  /**
   * Position in the agent's trigger-context summary. Absent means the variable
   * is not summarised. Free-form body text ranks last so truncation hits it.
   */
  agentContext?: number;
  /**
   * Holds a single address that names a participant of the message that fired
   * the trigger. A send step may use it as the WHOLE recipient entry; the same
   * address arriving through a step output or inside a larger template is not
   * exempt.
   */
  participantAddress?: boolean;
}

const EMAIL_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: EmailVariable.SUBJECT,
    alias: EmailVariablePlain.SUBJECT,
    labelKey: 'flowEditor.variables.emailSubject',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 1,
  },
  {
    key: EmailVariable.BODY,
    alias: EmailVariablePlain.BODY,
    labelKey: 'flowEditor.variables.emailBody',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 7,
  },
  {
    key: EmailVariable.DATE_TIME,
    alias: EmailVariablePlain.DATE_TIME,
    labelKey: 'flowEditor.variables.emailDateTime',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
  {
    key: EmailVariable.TO,
    alias: EmailVariablePlain.TO,
    labelKey: 'flowEditor.variables.emailTo',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
  {
    key: EmailVariable.CC,
    alias: EmailVariablePlain.CC,
    labelKey: 'flowEditor.variables.emailCc',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 5,
  },
  {
    key: EmailVariable.SENDER_NAME,
    alias: EmailVariablePlain.SENDER_NAME,
    labelKey: 'flowEditor.variables.senderDisplayName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 2,
  },
  {
    key: EmailVariable.SENDER_EMAIL,
    alias: EmailVariablePlain.SENDER_EMAIL,
    labelKey: 'flowEditor.variables.senderEmail',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 3,
    participantAddress: true,
  },
  // Seeded '' for an email without attachments; unset only when extraction throws.
  {
    key: EmailVariable.ATTACHMENTS,
    labelKey: 'flowEditor.variables.emailAttachments',
    trust: StarterVariableTrust.EXTERNAL,
  },
  {
    key: EmailVariable.MESSAGE_ID,
    alias: EmailVariablePlain.MESSAGE_ID,
    labelKey: 'flowEditor.variables.emailMessageId',
    trust: StarterVariableTrust.EXTERNAL,
  },
  {
    key: EmailVariable.RECIPIENTS,
    alias: EmailVariablePlain.RECIPIENTS,
    labelKey: 'flowEditor.variables.emailRecipients',
    trust: StarterVariableTrust.EXTERNAL,
  },
];

// Offered under EMAIL because a calendar invite arrives as one. Every field is
// optional: a plain email carries no calendarEventData.
const CALENDAR_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterCalendarVariable.EVENT_ID,
    alias: CalendarVariable.EVENT_ID,
    labelKey: 'flowEditor.variables.calendarEventId',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
  },
  {
    key: StarterCalendarVariable.SUMMARY,
    alias: CalendarVariable.SUMMARY,
    labelKey: 'flowEditor.variables.calendarSummary',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
  },
  {
    key: StarterCalendarVariable.RESPONSE_STATUS,
    alias: CalendarVariable.RESPONSE_STATUS,
    labelKey: 'flowEditor.variables.calendarResponseStatus',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
  },
  {
    key: StarterCalendarVariable.ORGANIZER,
    alias: CalendarVariable.ORGANIZER,
    labelKey: 'flowEditor.variables.calendarOrganizer',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    participantAddress: true,
  },
  {
    key: StarterCalendarVariable.ORGANIZER_NAME,
    alias: CalendarVariable.ORGANIZER_NAME,
    labelKey: 'flowEditor.variables.calendarOrganizerName',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
  },
  {
    key: StarterCalendarVariable.START_TIME,
    alias: CalendarVariable.START_TIME,
    labelKey: 'flowEditor.variables.calendarStartTime',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
  },
  {
    key: StarterCalendarVariable.END_TIME,
    alias: CalendarVariable.END_TIME,
    labelKey: 'flowEditor.variables.calendarEndTime',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
  },
  {
    key: StarterCalendarVariable.TIME_ZONE,
    alias: CalendarVariable.TIME_ZONE,
    labelKey: 'flowEditor.variables.calendarTimeZone',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
  },
];

const SLACK_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterSlackVariable.MESSAGE,
    alias: SlackVariablePlain.MESSAGE,
    labelKey: 'flowEditor.variables.slackMessage',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 7,
  },
  {
    key: StarterSlackVariable.CHANNEL,
    alias: SlackVariablePlain.CHANNEL,
    labelKey: 'flowEditor.variables.slackChannel',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 1,
  },
  {
    key: StarterSlackVariable.CHANNEL_ID,
    alias: SlackVariablePlain.CHANNEL_ID,
    labelKey: 'flowEditor.variables.slackChannelId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  {
    key: StarterSlackVariable.WORKSPACE_ID,
    alias: SlackVariablePlain.WORKSPACE_ID,
    labelKey: 'flowEditor.variables.slackWorkspaceId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
  {
    key: StarterSlackVariable.AUTHOR,
    alias: SlackVariablePlain.AUTHOR,
    labelKey: 'flowEditor.variables.slackAuthor',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
  {
    key: StarterSlackVariable.AUTHOR_ID,
    alias: SlackVariablePlain.AUTHOR_ID,
    labelKey: 'flowEditor.variables.slackAuthorId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 5,
  },
  {
    key: StarterSlackVariable.DATE_TIME,
    alias: SlackVariablePlain.DATE_TIME,
    labelKey: 'flowEditor.variables.slackDateTime',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
  // Seeded by resolveSlackAttachmentsVariable in processRecord, and only when a
  // step references it — the extraction re-downloads from Slack every run.
  {
    key: StarterSlackVariable.ATTACHMENTS,
    labelKey: 'flowEditor.variables.slackAttachments',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
  },
];

const GOOGLE_CHAT_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterGoogleChatVariable.MESSAGE,
    alias: GoogleChatVariablePlain.MESSAGE,
    labelKey: 'flowEditor.variables.googleChatMessage',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 10,
  },
  {
    key: StarterGoogleChatVariable.SPACE_NAME,
    alias: GoogleChatVariablePlain.SPACE_NAME,
    labelKey: 'flowEditor.variables.googleChatSpaceName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 1,
  },
  {
    key: StarterGoogleChatVariable.SPACE_ID,
    alias: GoogleChatVariablePlain.SPACE_ID,
    labelKey: 'flowEditor.variables.googleChatSpaceId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  {
    key: StarterGoogleChatVariable.SPACE_TYPE,
    alias: GoogleChatVariablePlain.SPACE_TYPE,
    labelKey: 'flowEditor.variables.googleChatSpaceType',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
  {
    key: StarterGoogleChatVariable.EMAIL_ADDRESS,
    alias: GoogleChatVariablePlain.EMAIL_ADDRESS,
    labelKey: 'flowEditor.variables.googleChatEmailAddress',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
  {
    key: StarterGoogleChatVariable.AUTHOR,
    alias: GoogleChatVariablePlain.AUTHOR,
    labelKey: 'flowEditor.variables.googleChatAuthor',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 5,
  },
  {
    key: StarterGoogleChatVariable.AUTHOR_ID,
    alias: GoogleChatVariablePlain.AUTHOR_ID,
    labelKey: 'flowEditor.variables.googleChatAuthorId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
  {
    key: StarterGoogleChatVariable.AUTHOR_EMAIL,
    alias: GoogleChatVariablePlain.AUTHOR_EMAIL,
    labelKey: 'flowEditor.variables.googleChatAuthorEmail',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 7,
    participantAddress: true,
  },
  {
    key: StarterGoogleChatVariable.THREAD_NAME,
    alias: GoogleChatVariablePlain.THREAD_NAME,
    labelKey: 'flowEditor.variables.googleChatThreadName',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 8,
  },
  {
    key: StarterGoogleChatVariable.DATE_TIME,
    alias: GoogleChatVariablePlain.DATE_TIME,
    labelKey: 'flowEditor.variables.googleChatDateTime',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 9,
  },
];

const INSTAGRAM_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterInstagramVariable.MESSAGE,
    alias: InstagramVariablePlain.MESSAGE,
    labelKey: 'flowEditor.variables.instagramMessage',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 9,
  },
  {
    key: StarterInstagramVariable.SENDER_NAME,
    alias: InstagramVariablePlain.SENDER_NAME,
    labelKey: 'flowEditor.variables.instagramSenderName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
  {
    key: StarterInstagramVariable.AUTHOR_ID,
    alias: InstagramVariablePlain.AUTHOR_ID,
    labelKey: 'flowEditor.variables.instagramAuthorId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 5,
  },
  {
    key: StarterInstagramVariable.CONVERSATION_ID,
    alias: InstagramVariablePlain.CONVERSATION_ID,
    labelKey: 'flowEditor.variables.instagramConversationId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
  {
    key: StarterInstagramVariable.ACCOUNT_ID,
    alias: InstagramVariablePlain.ACCOUNT_ID,
    labelKey: 'flowEditor.variables.instagramAccountId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  {
    key: StarterInstagramVariable.ACCOUNT_USERNAME,
    alias: InstagramVariablePlain.ACCOUNT_USERNAME,
    labelKey: 'flowEditor.variables.instagramAccountUsername',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 1,
  },
  {
    key: StarterInstagramVariable.DATE_TIME,
    alias: InstagramVariablePlain.DATE_TIME,
    labelKey: 'flowEditor.variables.instagramDateTime',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
  // A plain DM is not a story reply, so neither key exists on most fires.
  {
    key: StarterInstagramStoryVariable.STORY_ID,
    labelKey: 'flowEditor.variables.instagramStoryId',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
    agentContext: 7,
  },
  {
    key: StarterInstagramStoryVariable.STORY_URL,
    labelKey: 'flowEditor.variables.instagramStoryUrl',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
    agentContext: 8,
  },
];

const INSTAGRAM_CONTENT_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterInstagramContentVariable.POST_ID,
    labelKey: 'flowEditor.variables.instagramContentPostId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  {
    key: StarterInstagramContentVariable.COMMENT_ID,
    labelKey: 'flowEditor.variables.instagramContentCommentId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  // Absent on a top-level comment; present only on a reply to another comment.
  {
    key: StarterInstagramContentVariable.PARENT_COMMENT_ID,
    labelKey: 'flowEditor.variables.instagramContentParentCommentId',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
    agentContext: 3,
  },
  {
    key: StarterInstagramContentVariable.COMMENT_TEXT,
    labelKey: 'flowEditor.variables.instagramContentCommentText',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 5,
  },
  {
    key: StarterInstagramContentVariable.AUTHOR_USERNAME,
    labelKey: 'flowEditor.variables.instagramContentAuthorUsername',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
];

const WHATSAPP_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterWhatsAppVariable.MESSAGE,
    alias: WhatsAppVariablePlain.MESSAGE,
    labelKey: 'flowEditor.variables.whatsappMessage',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 7,
  },
  {
    key: StarterWhatsAppVariable.SENDER_NAME,
    alias: WhatsAppVariablePlain.SENDER_NAME,
    labelKey: 'flowEditor.variables.whatsappSenderName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 1,
  },
  {
    key: StarterWhatsAppVariable.SENDER_USERNAME,
    alias: WhatsAppVariablePlain.SENDER_USERNAME,
    labelKey: 'flowEditor.variables.whatsappSenderUsername',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 2,
  },
  {
    key: StarterWhatsAppVariable.COUNTERPART_ID,
    alias: WhatsAppVariablePlain.COUNTERPART_ID,
    labelKey: 'flowEditor.variables.whatsappCounterpartId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
  {
    key: StarterWhatsAppVariable.PHONE_NUMBER_ID,
    alias: WhatsAppVariablePlain.PHONE_NUMBER_ID,
    labelKey: 'flowEditor.variables.whatsappPhoneNumberId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 4,
  },
  {
    key: StarterWhatsAppVariable.DISPLAY_PHONE_NUMBER,
    alias: WhatsAppVariablePlain.DISPLAY_PHONE_NUMBER,
    labelKey: 'flowEditor.variables.whatsappDisplayPhoneNumber',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 5,
  },
  {
    key: StarterWhatsAppVariable.DATE_TIME,
    alias: WhatsAppVariablePlain.DATE_TIME,
    labelKey: 'flowEditor.variables.whatsappDateTime',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
];

// One TriggerType spans ten DoBox trigger keys, and each key populates its own
// slice. Only the entry id survives every one of them.
const DOBOX_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: DoBoxVariable.INBOX_ENTRY_ID,
    alias: DoBoxVariablePlain.INBOX_ENTRY_ID,
    labelKey: 'flowEditor.variables.doboxInboxEntryId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  {
    key: DoBoxVariable.SUBJECT,
    alias: DoBoxVariablePlain.SUBJECT,
    labelKey: 'flowEditor.variables.doboxSubject',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 2,
  },
  {
    key: DoBoxVariable.DESCRIPTION,
    alias: DoBoxVariablePlain.DESCRIPTION,
    labelKey: 'flowEditor.variables.doboxDescription',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 3,
  },
  {
    key: DoBoxVariable.TASK_GROUP_NAME,
    alias: DoBoxVariablePlain.TASK_GROUP_NAME,
    labelKey: 'flowEditor.variables.doboxTaskGroupName',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 4,
  },
  {
    key: DoBoxVariable.TASK_LABEL_NAMES,
    alias: DoBoxVariablePlain.TASK_LABEL_NAMES,
    labelKey: 'flowEditor.variables.doboxTaskLabelNames',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 5,
  },
  {
    key: DoBoxVariable.DUE_AT,
    alias: DoBoxVariablePlain.DUE_AT,
    labelKey: 'flowEditor.variables.doboxDueAt',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
    agentContext: 6,
  },
  {
    key: DoBoxVariable.ADDED_PARTICIPANT_NAMES,
    alias: DoBoxVariablePlain.ADDED_PARTICIPANT_NAMES,
    labelKey: 'flowEditor.variables.doboxAddedParticipantNames',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 10,
  },
  {
    key: DoBoxVariable.ADDED_LABEL_NAMES,
    alias: DoBoxVariablePlain.ADDED_LABEL_NAMES,
    labelKey: 'flowEditor.variables.doboxAddedLabelNames',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 11,
  },
  {
    key: DoBoxVariable.REMOVED_LABEL_NAMES,
    alias: DoBoxVariablePlain.REMOVED_LABEL_NAMES,
    labelKey: 'flowEditor.variables.doboxRemovedLabelNames',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 12,
  },
  {
    key: DoBoxVariable.NEW_TASK_GROUP_NAME,
    alias: DoBoxVariablePlain.NEW_TASK_GROUP_NAME,
    labelKey: 'flowEditor.variables.doboxNewTaskGroupName',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 13,
  },
  {
    key: DoBoxVariable.COMMENT_ID,
    alias: DoBoxVariablePlain.COMMENT_ID,
    labelKey: 'flowEditor.variables.doboxCommentId',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
    agentContext: 7,
  },
  {
    key: DoBoxVariable.COMMENT_MESSAGE,
    alias: DoBoxVariablePlain.COMMENT_MESSAGE,
    labelKey: 'flowEditor.variables.doboxCommentMessage',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 8,
  },
  {
    key: DoBoxVariable.COMMENT_AUTHOR,
    alias: DoBoxVariablePlain.COMMENT_AUTHOR,
    labelKey: 'flowEditor.variables.doboxCommentAuthor',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 9,
  },
];

const SCHEDULE_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterScheduleVariable.FIRED_AT,
    labelKey: 'flowEditor.variables.scheduleFiredAt',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  {
    key: StarterScheduleVariable.FIRE_DATE_LOCAL,
    labelKey: 'flowEditor.variables.scheduleFireDateLocal',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  {
    key: StarterScheduleVariable.FIRE_TIME_LOCAL,
    labelKey: 'flowEditor.variables.scheduleFireTimeLocal',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
];

const MANUAL_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterManualVariable.TRIGGERED_AT,
    labelKey: 'flowEditor.variables.manualTriggeredAt',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  {
    key: StarterManualVariable.TRIGGERED_BY_USER_ID,
    labelKey: 'flowEditor.variables.manualTriggeredByUserId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
];

const KNOWLEDGE_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterKnowledgeVariable.PAGE_TITLE,
    labelKey: 'flowEditor.variables.knowledgePageTitle',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 3,
  },
  {
    key: StarterKnowledgeVariable.PAGE_PATH,
    labelKey: 'flowEditor.variables.knowledgePagePath',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
  {
    key: StarterKnowledgeVariable.PAGE_CONTENT,
    labelKey: 'flowEditor.variables.knowledgePageContent',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 9,
  },
  {
    key: StarterKnowledgeVariable.PAGE_ID,
    labelKey: 'flowEditor.variables.knowledgePageId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  {
    key: StarterKnowledgeVariable.SPACE_ID,
    labelKey: 'flowEditor.variables.knowledgeSpaceId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  {
    key: StarterKnowledgeVariable.PAGE_VERSION,
    labelKey: 'flowEditor.variables.knowledgePageVersion',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 8,
  },
  {
    key: StarterKnowledgeVariable.PARENT_PAGE_ID,
    labelKey: 'flowEditor.variables.knowledgeParentPageId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 5,
  },
  {
    key: StarterKnowledgeVariable.OWNER_TYPE,
    labelKey: 'flowEditor.variables.knowledgeOwnerType',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
  {
    key: StarterKnowledgeVariable.OWNER_ID,
    labelKey: 'flowEditor.variables.knowledgeOwnerId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 7,
  },
];

const GITHUB_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterGitHubVariable.EVENT,
    alias: GitHubVariablePlain.EVENT,
    labelKey: 'flowEditor.variables.githubEvent',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  {
    key: StarterGitHubVariable.ACTION,
    alias: GitHubVariablePlain.ACTION,
    labelKey: 'flowEditor.variables.githubAction',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  {
    key: StarterGitHubVariable.REPOSITORY,
    alias: GitHubVariablePlain.REPOSITORY,
    labelKey: 'flowEditor.variables.githubRepository',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 3,
  },
  {
    key: StarterGitHubVariable.URL,
    alias: GitHubVariablePlain.URL,
    labelKey: 'flowEditor.variables.githubUrl',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 5,
  },
  {
    key: StarterGitHubVariable.AUTHOR,
    alias: GitHubVariablePlain.AUTHOR,
    labelKey: 'flowEditor.variables.githubAuthor',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 6,
  },
  {
    key: StarterGitHubVariable.BODY,
    alias: GitHubVariablePlain.BODY,
    labelKey: 'flowEditor.variables.githubBody',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 7,
  },
  // Only push and release populate it — the label says branch/tag rather than
  // "ref" because that is how the trigger's own filter field is labelled.
  {
    key: StarterGitHubVariable.REF,
    alias: GitHubVariablePlain.REF,
    labelKey: 'flowEditor.variables.githubRef',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 4,
  },
];

// Plain `person.*` namespace by design — StarterPeopleVariable holds the bare
// keys and personEventResolution dual-seeds the starter.-prefixed twin, so the
// registry stores the canonical form and the bare one as its alias.
const PEOPLE_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: `starter.${StarterPeopleVariable.ID}`,
    alias: StarterPeopleVariable.ID,
    labelKey: 'flowEditor.variables.personId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  {
    key: `starter.${StarterPeopleVariable.DISPLAY_NAME}`,
    alias: StarterPeopleVariable.DISPLAY_NAME,
    labelKey: 'flowEditor.variables.personDisplayName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 2,
  },
  {
    key: `starter.${StarterPeopleVariable.EMAILS}`,
    alias: StarterPeopleVariable.EMAILS,
    labelKey: 'flowEditor.variables.personEmails',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 3,
  },
  {
    key: `starter.${StarterPeopleVariable.PHONES}`,
    alias: StarterPeopleVariable.PHONES,
    labelKey: 'flowEditor.variables.personPhones',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
  {
    key: `starter.${StarterPeopleVariable.JOB_TITLE}`,
    alias: StarterPeopleVariable.JOB_TITLE,
    labelKey: 'flowEditor.variables.personJobTitle',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 5,
  },
  {
    key: `starter.${StarterPeopleVariable.ORIGIN}`,
    alias: StarterPeopleVariable.ORIGIN,
    labelKey: 'flowEditor.variables.personOrigin',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
  {
    key: `starter.${StarterPeopleVariable.TEAM_ID}`,
    alias: StarterPeopleVariable.TEAM_ID,
    labelKey: 'flowEditor.variables.personTeamId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 7,
  },
  {
    key: `starter.${StarterPeopleVariable.NOTES}`,
    alias: StarterPeopleVariable.NOTES,
    labelKey: 'flowEditor.variables.personNotes',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 8,
  },
];

const GBP_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterGbpVariable.REVIEW_NAME,
    labelKey: 'flowEditor.variables.gbpReviewName',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterGbpVariable.REVIEW_ID,
    labelKey: 'flowEditor.variables.gbpReviewId',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterGbpVariable.RATING,
    labelKey: 'flowEditor.variables.gbpRating',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
  {
    key: StarterGbpVariable.AUTHOR_NAME,
    labelKey: 'flowEditor.variables.gbpAuthorName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 2,
  },
  {
    key: StarterGbpVariable.REVIEW_TEXT,
    labelKey: 'flowEditor.variables.gbpReviewText',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 7,
  },
  {
    key: StarterGbpVariable.LOCATION_NAME,
    labelKey: 'flowEditor.variables.gbpLocationName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 1,
  },
  {
    key: StarterGbpVariable.IS_UPDATE,
    labelKey: 'flowEditor.variables.gbpIsUpdate',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 4,
  },
  {
    key: StarterGbpVariable.EXISTING_REPLY,
    labelKey: 'flowEditor.variables.gbpExistingReply',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 5,
  },
  {
    key: StarterGbpVariable.CREATED_AT,
    labelKey: 'flowEditor.variables.gbpCreatedAt',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
];

const TEAMS_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterTeamsVariable.MESSAGE,
    alias: TeamsVariablePlain.MESSAGE,
    labelKey: 'flowEditor.variables.teamsMessage',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 7,
  },
  {
    key: StarterTeamsVariable.CHAT_ID,
    alias: TeamsVariablePlain.CHAT_ID,
    labelKey: 'flowEditor.variables.teamsChatId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  // The "${chatType} - ${topic}" prefix is stripped by the seed; absent when the row has no topic to strip from.
  {
    key: StarterTeamsVariable.CHAT_NAME,
    alias: TeamsVariablePlain.CHAT_NAME,
    labelKey: 'flowEditor.variables.teamsChatName',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 2,
  },
  {
    key: StarterTeamsVariable.CHAT_TYPE,
    alias: TeamsVariablePlain.CHAT_TYPE,
    labelKey: 'flowEditor.variables.teamsChatType',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
  {
    key: StarterTeamsVariable.AUTHOR_NAME,
    alias: TeamsVariablePlain.AUTHOR_NAME,
    labelKey: 'flowEditor.variables.teamsAuthorName',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 4,
  },
  // Optional by construction for guests/federated members — see spec fact 6c.
  {
    key: StarterTeamsVariable.AUTHOR_EMAIL,
    alias: TeamsVariablePlain.AUTHOR_EMAIL,
    labelKey: 'flowEditor.variables.teamsAuthorEmail',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 5,
    participantAddress: true,
  },
  {
    key: StarterTeamsVariable.MESSAGE_URL,
    alias: TeamsVariablePlain.MESSAGE_URL,
    labelKey: 'flowEditor.variables.teamsMessageUrl',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
    agentContext: 8,
  },
  // '' rather than undefined when the tenant is unresolved — never seeded as missing.
  {
    key: StarterTeamsVariable.TENANT_ID,
    alias: TeamsVariablePlain.TENANT_ID,
    labelKey: 'flowEditor.variables.teamsTenantId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 9,
  },
  {
    key: StarterTeamsVariable.HOME_ACCOUNT_ID,
    alias: TeamsVariablePlain.HOME_ACCOUNT_ID,
    labelKey: 'flowEditor.variables.teamsHomeAccountId',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
  },
  {
    key: StarterTeamsVariable.MESSAGE_ID,
    alias: TeamsVariablePlain.MESSAGE_ID,
    labelKey: 'flowEditor.variables.teamsMessageId',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterTeamsVariable.RECEIVED_AT,
    alias: TeamsVariablePlain.RECEIVED_AT,
    labelKey: 'flowEditor.variables.teamsReceivedAt',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
];

const FACEBOOK_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterFacebookVariable.COMMENT_ID,
    alias: FacebookVariablePlain.COMMENT_ID,
    labelKey: 'flowEditor.variables.facebookCommentId',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterFacebookVariable.COMMENT_TEXT,
    alias: FacebookVariablePlain.COMMENT_TEXT,
    labelKey: 'flowEditor.variables.facebookCommentText',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
  {
    key: StarterFacebookVariable.POST_ID,
    alias: FacebookVariablePlain.POST_ID,
    labelKey: 'flowEditor.variables.facebookPostId',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterFacebookVariable.PAGE_ID,
    alias: FacebookVariablePlain.PAGE_ID,
    labelKey: 'flowEditor.variables.facebookPageId',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterFacebookVariable.PAGE_NAME,
    alias: FacebookVariablePlain.PAGE_NAME,
    labelKey: 'flowEditor.variables.facebookPageName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 1,
  },
  {
    key: StarterFacebookVariable.AUTHOR_NAME,
    alias: FacebookVariablePlain.AUTHOR_NAME,
    labelKey: 'flowEditor.variables.facebookAuthorName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 2,
  },
  {
    key: StarterFacebookVariable.AUTHOR_ID,
    alias: FacebookVariablePlain.AUTHOR_ID,
    labelKey: 'flowEditor.variables.facebookAuthorId',
    trust: StarterVariableTrust.SYSTEM,
  },
  // Absent on a top-level comment; present only on a reply to another comment.
  {
    key: StarterFacebookVariable.PARENT_COMMENT_ID,
    alias: FacebookVariablePlain.PARENT_COMMENT_ID,
    labelKey: 'flowEditor.variables.facebookParentCommentId',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
  },
  {
    key: StarterFacebookVariable.DATE_TIME,
    alias: FacebookVariablePlain.DATE_TIME,
    labelKey: 'flowEditor.variables.facebookDateTime',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
];

const FACEBOOK_MESSENGER_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterFacebookMessengerVariable.MESSAGE,
    alias: FacebookMessengerVariablePlain.MESSAGE,
    labelKey: 'flowEditor.variables.facebookMessengerMessage',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 7,
  },
  {
    key: StarterFacebookMessengerVariable.SENDER_NAME,
    alias: FacebookMessengerVariablePlain.SENDER_NAME,
    labelKey: 'flowEditor.variables.facebookMessengerSenderName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 4,
  },
  {
    key: StarterFacebookMessengerVariable.AUTHOR_ID,
    alias: FacebookMessengerVariablePlain.AUTHOR_ID,
    labelKey: 'flowEditor.variables.facebookMessengerAuthorId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 5,
  },
  {
    key: StarterFacebookMessengerVariable.CONVERSATION_ID,
    alias: FacebookMessengerVariablePlain.CONVERSATION_ID,
    labelKey: 'flowEditor.variables.facebookMessengerConversationId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
  {
    key: StarterFacebookMessengerVariable.PAGE_ID,
    alias: FacebookMessengerVariablePlain.PAGE_ID,
    labelKey: 'flowEditor.variables.facebookMessengerPageId',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  {
    key: StarterFacebookMessengerVariable.PAGE_NAME,
    alias: FacebookMessengerVariablePlain.PAGE_NAME,
    labelKey: 'flowEditor.variables.facebookMessengerPageName',
    trust: StarterVariableTrust.EXTERNAL,
    agentContext: 1,
  },
  {
    key: StarterFacebookMessengerVariable.DATE_TIME,
    alias: FacebookMessengerVariablePlain.DATE_TIME,
    labelKey: 'flowEditor.variables.facebookMessengerDateTime',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
];

// The entity name and the summary are derived from workspace content that originates outside it (mail, chat), so they are EXTERNAL; everything else is an id, a level or a platform-built string.
const BRAIN_SIGNAL_VARIABLES: readonly StarterVariableDef[] = [
  {
    key: StarterBrainSignalVariable.NAME,
    labelKey: 'flowEditor.variables.signalName',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 1,
  },
  {
    key: StarterBrainSignalVariable.DEFINITION_ID,
    labelKey: 'flowEditor.variables.signalDefinitionId',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterBrainSignalVariable.TEMPLATE_ID,
    labelKey: 'flowEditor.variables.signalTemplateId',
    trust: StarterVariableTrust.SYSTEM,
    optional: true,
  },
  {
    key: StarterBrainSignalVariable.TRIGGER_KEY,
    labelKey: 'flowEditor.variables.signalTriggerKey',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterBrainSignalVariable.LEVEL,
    labelKey: 'flowEditor.variables.signalLevel',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 2,
  },
  {
    key: StarterBrainSignalVariable.PREVIOUS_LEVEL,
    labelKey: 'flowEditor.variables.signalPreviousLevel',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 3,
  },
  {
    key: StarterBrainSignalVariable.STATUS,
    labelKey: 'flowEditor.variables.signalStatus',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterBrainSignalVariable.ENTITY_ID,
    labelKey: 'flowEditor.variables.signalEntityId',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterBrainSignalVariable.ENTITY_TYPE,
    labelKey: 'flowEditor.variables.signalEntityType',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterBrainSignalVariable.ENTITY_NAME,
    labelKey: 'flowEditor.variables.signalEntityName',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 4,
  },
  {
    key: StarterBrainSignalVariable.SUMMARY,
    labelKey: 'flowEditor.variables.signalSummary',
    trust: StarterVariableTrust.EXTERNAL,
    optional: true,
    agentContext: 8,
  },
  {
    key: StarterBrainSignalVariable.REASON_CODES,
    labelKey: 'flowEditor.variables.signalReasonCodes',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 5,
  },
  {
    key: StarterBrainSignalVariable.EVIDENCE,
    labelKey: 'flowEditor.variables.signalEvidence',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 6,
  },
  {
    key: StarterBrainSignalVariable.OPENED_AT,
    labelKey: 'flowEditor.variables.signalOpenedAt',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterBrainSignalVariable.LAST_CHANGE_AT,
    labelKey: 'flowEditor.variables.signalLastChangeAt',
    trust: StarterVariableTrust.SYSTEM,
  },
  {
    key: StarterBrainSignalVariable.LINK,
    labelKey: 'flowEditor.variables.signalLink',
    trust: StarterVariableTrust.SYSTEM,
    agentContext: 7,
  },
  {
    key: StarterBrainSignalVariable.TEAM_ID,
    labelKey: 'flowEditor.variables.signalTeamId',
    trust: StarterVariableTrust.SYSTEM,
  },
];

/**
 * The one declaration of a trigger family's variables. Every surface that names
 * them reads from here: the editor's picker and pill labels, the AI generator's
 * template-variable validation and prompt, and the agent's trigger summary. The
 * resolution strategies are the one consumer that cannot be derived, so
 * starterVariableCoverage.test.ts asserts they seed every non-optional entry.
 */
export const STARTER_VARIABLE_REGISTRY: Record<
  TriggerType,
  readonly StarterVariableDef[]
> = {
  [TriggerType.EMAIL]: [...EMAIL_VARIABLES, ...CALENDAR_VARIABLES],
  [TriggerType.SLACK]: SLACK_VARIABLES,
  [TriggerType.GOOGLE_CHAT]: GOOGLE_CHAT_VARIABLES,
  [TriggerType.INSTAGRAM]: INSTAGRAM_VARIABLES,
  [TriggerType.INSTAGRAM_CONTENT]: INSTAGRAM_CONTENT_VARIABLES,
  [TriggerType.SCHEDULE]: SCHEDULE_VARIABLES,
  [TriggerType.DOBOX]: DOBOX_VARIABLES,
  [TriggerType.MANUAL]: MANUAL_VARIABLES,
  [TriggerType.WHATSAPP]: WHATSAPP_VARIABLES,
  [TriggerType.KNOWLEDGE]: KNOWLEDGE_VARIABLES,
  [TriggerType.GITHUB]: GITHUB_VARIABLES,
  [TriggerType.PEOPLE]: PEOPLE_VARIABLES,
  [TriggerType.GOOGLE_BUSINESS]: GBP_VARIABLES,
  [TriggerType.FACEBOOK]: FACEBOOK_VARIABLES,
  [TriggerType.FACEBOOK_MESSENGER]: FACEBOOK_MESSENGER_VARIABLES,
  [TriggerType.TEAMS]: TEAMS_VARIABLES,
  [TriggerType.BRAIN_SIGNAL]: BRAIN_SIGNAL_VARIABLES,
};

/** Every key and alias a reference may legitimately use, for one trigger type. */
export const getStarterVariableKeys = (
  triggerType: TriggerType,
): readonly string[] =>
  STARTER_VARIABLE_REGISTRY[triggerType].flatMap((variable) =>
    variable.alias ? [variable.key, variable.alias] : [variable.key],
  );

/** Trigger-summary keys for one trigger type, in display order. */
export const getAgentContextKeys = (
  triggerType: TriggerType,
): readonly string[] =>
  STARTER_VARIABLE_REGISTRY[triggerType]
    .flatMap((variable) =>
      variable.agentContext === undefined
        ? []
        : [{ key: variable.key, rank: variable.agentContext }],
    )
    .sort((a, b) => a.rank - b.rank)
    .map((entry) => entry.key);

const TRUST_BY_KEY: ReadonlyMap<string, StarterVariableTrust> = new Map(
  Object.values(STARTER_VARIABLE_REGISTRY)
    .flat()
    .flatMap((variable) =>
      variable.alias
        ? [
            [variable.key, variable.trust] as const,
            [variable.alias, variable.trust] as const,
          ]
        : [[variable.key, variable.trust] as const],
    ),
);

/** Trust of a registry key or alias; undefined when the key is not a starter variable. */
export const getStarterVariableTrust = (
  key: string,
): StarterVariableTrust | undefined => TRUST_BY_KEY.get(key);

const PARTICIPANT_ADDRESS_KEYS: ReadonlySet<string> = new Set(
  Object.values(STARTER_VARIABLE_REGISTRY)
    .flat()
    .filter((variable) => variable.participantAddress)
    .flatMap((variable) =>
      variable.alias ? [variable.key, variable.alias] : [variable.key],
    ),
);

/** True for a registry key or alias that holds one participant's address. */
export const isParticipantAddressVariable = (key: string): boolean =>
  PARTICIPANT_ADDRESS_KEYS.has(key);
